import { captureException, captureMessage } from '@sentry/react';
import { ErrorLogProps, ComboBoxPrediction, ErrorLevel } from '.';
import {
  Disciplines,
  AvailabilityFilters,
  OpeningBadgeStatus,
} from './constants';
import { hasFeatureFlag } from '@janeapp/feature-flags-client';

export const browserSupportsGeolocation = () =>
  Boolean('geolocation' in navigator);

export const buildDisciplineBookingUrl = (
  bookingUrl: string,
  discipline: string
) => {
  const disciplineSlug = discipline.toLowerCase().replace(/_/g, '-');
  return `${bookingUrl}#${disciplineSlug}`;
};

export const humanReadableDiscipline = (discipline: string): string => {
  const humanReadableValue = discipline.toLowerCase().replace(/_/g, ' ');
  return (
    humanReadableValue.charAt(0).toUpperCase() + humanReadableValue.slice(1)
  );
};
export const generateDisciplineList = (): ComboBoxPrediction[] =>
  Object.entries(Disciplines).map(([key, value]) => ({
    label: value,
    value: key,
  }));

export const buildPractitionerBookingUrl = (
  staffMemberGuid: string,
  bookingBaseUrl?: string,
  rudderstackSessionId?: string
) => {
  if (!bookingBaseUrl) return undefined;
  const staffId = staffMemberGuid.split('-')[1];
  return appendDiscoveryTokenToBookingUrl(
    `${bookingBaseUrl}#/staff_member/${staffId}`,
    rudderstackSessionId
  );
};

export const buildPractitionerBookingUrlWithTreatmentId = (
  staffMemberGuid: string,
  bookingBaseUrl?: string,
  treatmentId?: number,
  rudderstackSessionId?: string
) => {
  if (!bookingBaseUrl) return undefined;
  if (!treatmentId) return undefined;
  const staffId = staffMemberGuid.split('-')[1];
  return appendDiscoveryTokenToBookingUrl(
    `${bookingBaseUrl}#/staff_member/${staffId}/treatment/${treatmentId}`,
    rudderstackSessionId
  );
};

export const appendDiscoveryTokenToBookingUrl = (
  baseUrl?: string,
  rudderstackSessionId?: string
) => {
  if (!baseUrl) return undefined;
  if (!rudderstackSessionId) return undefined;
  const url = new URL(baseUrl);
  url.searchParams.append('practitioner_search_token', rudderstackSessionId);
  return url.toString();
};

export const formatRelativeOpeningDate = (date: Date): string => {
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  const todayDate = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  const tomorrowDate = new Date(
    tomorrow.getFullYear(),
    tomorrow.getMonth(),
    tomorrow.getDate()
  );
  const targetDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );

  if (targetDate.getTime() === todayDate.getTime()) {
    return 'Today';
  } else if (targetDate.getTime() === tomorrowDate.getTime()) {
    return 'Tomorrow';
  } else {
    return date.toLocaleDateString('en-US', { weekday: 'long' });
  }
};

export const formatFullOpeningDate = (date: Date): string => {
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

export const formatOpeningTime = (date: Date): string => {
  return date
    .toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    })
    .toLowerCase();
};

export const logErrorToSentry = ({ type, message }: ErrorLogProps) => {
  if (type === ErrorLevel.Error)
    captureException(new Error(message), { level: 'error' });
  if (type === ErrorLevel.Warning)
    captureMessage(message, { level: 'warning' });
};

//get distance between center and bounds to pass as distanceKm to /locations
export const calculateDistanceKm = (
  center: google.maps.LatLngLiteral,
  bounds: google.maps.LatLngBoundsLiteral
): number => {
  const centerLat = deg2rad(center.lat);
  const centerLng = deg2rad(center.lng);

  const boundsNorth = bounds.north;
  const boundsEast = bounds.east;

  const R = 6371; // Earth's radius in kilometers
  const dLat = deg2rad(boundsNorth - centerLat);
  const dLng = deg2rad(boundsEast - centerLng);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(centerLat)) *
      Math.cos(deg2rad(boundsNorth)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c; // Distance in kilometers

  return distance;
};

function deg2rad(deg: number): number {
  return deg * (Math.PI / 180);
}

export const formatDistance = (distanceInMeters: number): string => {
  return distanceInMeters > 1000
    ? `${(distanceInMeters / 1000).toFixed(1)}km away`
    : `${distanceInMeters.toFixed(0)}m away`;
};

export const formatAddress = (
  streetAddress2 = '',
  streetAddress = '',
  city = '',
  province = ''
) => {
  return (
    [streetAddress2, streetAddress, city, province]
      .map((part) => (typeof part === 'string' ? part.trim() : ''))
      // Trim empty spaces before , characters
      .filter((part) => part !== '')
      .join(', ')
  );
};

// calculate a bounding box around a central point, for initial map search
//distance = kilometers from the center to each side of the bounding box
export const calculateBoundingBox = (
  { lat, lng }: google.maps.LatLngLiteral,
  distance: number
) => {
  const toRadians = (degrees: number) => (degrees * Math.PI) / 180;
  const toDegrees = (radians: number) => (radians * 180) / Math.PI;
  const earthRadius = 6371; // Earth's radius in kilometers

  const angularDistance = distance / earthRadius;
  const latInRadians = toRadians(lat);
  const lonInRadians = toRadians(lng);

  const minLat = latInRadians - angularDistance;
  const maxLat = latInRadians + angularDistance;
  const minLon =
    lonInRadians -
    Math.asin(Math.sin(angularDistance) / Math.cos(latInRadians));
  const maxLon =
    lonInRadians +
    Math.asin(Math.sin(angularDistance) / Math.cos(latInRadians));

  return {
    north: toDegrees(maxLat),
    south: toDegrees(minLat),
    east: toDegrees(maxLon),
    west: toDegrees(minLon),
  };
};

export const getDateRangeForAvailability = (
  availability: AvailabilityFilters
) => {
  switch (availability) {
    case AvailabilityFilters.Today: {
      const today = new Date();
      const endOfDay = new Date(today);
      endOfDay.setHours(23, 59, 59, 999);

      return {
        startDateUtc: today.toISOString(),
        endDateUtc: endOfDay.toISOString(),
      };
    }

    case AvailabilityFilters.Tomorrow: {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);
      const startOfTomorrow = new Date(tomorrow);
      const endOfTomorrow = new Date(tomorrow);
      endOfTomorrow.setHours(23, 59, 59, 999);
      return {
        startDateUtc: startOfTomorrow.toISOString(),
        endDateUtc: endOfTomorrow.toISOString(),
      };
    }

    case AvailabilityFilters.WithinAWeek: {
      const today = new Date();
      const endOfWeek = new Date(today);
      endOfWeek.setDate(endOfWeek.getDate() + 7);
      endOfWeek.setHours(23, 59, 59, 999);
      return {
        startDateUtc: today.toISOString(),
        endDateUtc: endOfWeek.toISOString(),
      };
    }

    default:
      return {
        startDateUtc: undefined,
        endDateUtc: undefined,
      };
  }
};
export interface SearchState {
  discipline: string;
  location?: string; //TODO change to not undefined
  latLng?: google.maps.LatLngLiteral; //TODO change to not undefined
  availability?: AvailabilityFilters;
  mapBounds?: object;
  mapInteractionActive?: boolean;
}

export const buildSearchUrl = (state: SearchState): string => {
  const params = new URLSearchParams();

  params.set('discipline', state.discipline);

  const queryString = params.toString();
  return queryString ? `/search?${queryString}` : '/search';
};

// Source: https://www.notion.so/janeapp/North-Shore-Expansion-20f378ecd68380c6939cc8fa857919f6?source=copy_link#23e378ecd683800ab5d5db0436e33a4b
export const NORTH_SHORE_BOUNDS: google.maps.LatLngBoundsLiteral = {
  north: 49.407,
  south: 49.295,
  east: -122.932,
  west: -123.296,
};

export const SQUAMISH_BOUNDS: google.maps.LatLngBoundsLiteral = {
  north: 49.995510329792985,
  south: 49.642029196486476,
  east: -123.0110142206582,
  west: -123.36967172248022,
};

export const ALL_BC_BOUNDS: google.maps.LatLngBoundsLiteral = {
  // (min long, min lat, max long, max lat)
  // -139.06,   48.30,  -114.03,   60.00
  north: 60.0,
  south: 48.3,
  east: -114.03,
  west: -139.06,
};

export const getBounds = (): google.maps.LatLngBoundsLiteral => {
  if (hasFeatureFlag('ALL_BC_BETA')) {
    return ALL_BC_BOUNDS;
  }

  if (hasFeatureFlag('NORTH_VANCOUVER_BETA')) {
    // Use NS + Squamish bounds
    // This should make the bounding box cover all of Squamish and the North Shore.
    return {
      north: Math.max(NORTH_SHORE_BOUNDS.north, SQUAMISH_BOUNDS.north),
      south: Math.min(NORTH_SHORE_BOUNDS.south, SQUAMISH_BOUNDS.south),
      east: Math.max(NORTH_SHORE_BOUNDS.east, SQUAMISH_BOUNDS.east),
      west: Math.min(NORTH_SHORE_BOUNDS.west, SQUAMISH_BOUNDS.west),
    };
  }

  return SQUAMISH_BOUNDS;
};

export const formatDate = (date: string) => {
  const NOW = new Date();
  const TODAY = new Date().toDateString();
  const MS_IN_DAY = 24 * 60 * 60 * 1000;
  const TOMORROW = new Date(NOW.getTime() + MS_IN_DAY).toDateString();
  const ONE_WEEK_FROM_NOW = new Date(NOW.getTime() + 7 * MS_IN_DAY);
  const ONE_WEEK_FROM_NOW_END_OF_DAY = new Date(ONE_WEEK_FROM_NOW);
  ONE_WEEK_FROM_NOW_END_OF_DAY.setHours(23, 59, 59, 999);
  // date is an ISO8601 date time with offset, e.g. 2025-05-30T13:48-07:00
  const openingDate = new Date(date);
  const openingDateString = new Date(date).toDateString();

  if (NOW <= openingDate && openingDateString === TODAY) {
    return OpeningBadgeStatus.AVAILABLE_TODAY;
  } else if (openingDateString === TOMORROW) {
    return OpeningBadgeStatus.AVAILABLE_TOMORROW;
  } else if (
    NOW <= openingDate &&
    openingDate <= ONE_WEEK_FROM_NOW_END_OF_DAY
  ) {
    return OpeningBadgeStatus.AVAILABLE_THIS_WEEK;
  } else {
    // date may be more than one week in the future. This works with OpeningBadge.tsx:18 to make sure we don't show an element to the user.
    // If we showed an element, the user would see a small, empty blue box.
    return undefined;
  }
};
